// src/components/strategies/StrategySettings.jsx
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HiInformationCircle, HiSearch, HiExclamationCircle, HiChevronDown, HiShieldCheck, HiExclamation } from 'react-icons/hi';

const TooltipIcon = ({ text, position = 'left', side = 'top' }) => {
  const bubbleAlign = position === 'right' ? 'right-0' : 'left-0';
  const arrowAlign = position === 'right' ? 'right-1.5' : 'left-1.5';
  const verticalPos = side === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2';
  const arrowStyle = side === 'bottom' ? 'bottom-full border-b-slate-900/95' : 'top-full border-t-slate-900/95';
  return (
    <div className="group relative inline-block ml-1.5 cursor-help">
      <HiInformationCircle className="h-3.5 w-3.5 text-slate-500 group-hover:text-blue-400 transition-colors" />
      <div className={`absolute ${verticalPos} ${bubbleAlign} hidden group-hover:block w-48 p-2.5 bg-slate-900/95 backdrop-blur-sm text-[11px] leading-relaxed text-slate-200 rounded-lg shadow-2xl border border-slate-700/50 z-[150] pointer-events-none animate-in fade-in slide-in-from-${side === 'bottom' ? 'top' : 'bottom'}-1 duration-200 whitespace-normal break-words`}>
        {text}
        <div className={`absolute ${arrowStyle} ${arrowAlign} border-[5px] border-transparent`}></div>
      </div>
    </div>
  );
};

const InputField = ({ label, name, type = 'text', value, onChange, step, placeholder, tooltip, tooltipPosition, tooltipSide, readOnly, error }) => (
  <div>
    <div className="flex items-center mb-1.5">
      <label htmlFor={name} className="text-[13px] font-medium text-slate-300 block">{label}</label>
      {tooltip && <TooltipIcon text={tooltip} position={tooltipPosition} side={tooltipSide} />}
    </div>
    <input
      id={name} name={name} type={type} value={value} onChange={onChange}
      step={step} placeholder={placeholder} readOnly={readOnly}
      className={`w-full border rounded-lg text-white p-2.5 text-sm focus:outline-none focus:ring-2 transition-all placeholder:text-slate-500 
        ${error ? 'border-rose-500 focus:ring-rose-500/50 bg-rose-500/5' : 'border-slate-600/50 bg-slate-700/50 focus:ring-blue-500/50'}
        ${readOnly ? 'bg-slate-800/50 border-slate-700 text-slate-400 cursor-not-allowed' : ''}`}
    />
    {error && <p className="text-[10px] text-rose-400 mt-1">{error}</p>}
  </div>
);

const CustomSelect = ({ label, name, value, onChange, options, tooltip, tooltipPosition, tooltipSide }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (opt) => {
    onChange({ target: { name, value: opt } });
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="flex items-center mb-1.5">
        <label className="text-[13px] font-medium text-slate-300 block">{label}</label>
        {tooltip && <TooltipIcon text={tooltip} position={tooltipPosition} side={tooltipSide} />}
      </div>
      
      <div 
        className="relative cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg text-white p-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all select-none">
          {value}m
        </div>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500">
           <HiChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <ul className="absolute z-[110] w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl max-h-60 overflow-y-auto custom-scrollbar backdrop-blur-md">
          {options.map((opt) => (
            <li 
              key={opt} 
              onClick={() => handleSelect(opt)} 
              className={`px-4 py-2 text-sm cursor-pointer hover:bg-blue-600 hover:text-white transition-colors text-slate-300 ${String(opt) === String(value) ? 'bg-blue-500/20 text-blue-400' : ''}`}
            >
              {opt}m
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const SymbolAutocomplete = ({ label, name, value, onChange, options, placeholder, isLoading, tooltip, tooltipPosition, tooltipSide }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const wrapperRef = useRef(null);
  useEffect(() => {
    if (!options) return;
    const term = String(value).toUpperCase().trim();
    setFilteredOptions(!term ? options : options.filter(opt => opt.includes(term)));
  }, [value, options]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="flex items-center mb-1.5">
        <label htmlFor={name} className="text-[13px] font-medium text-slate-300 block">{label}</label>
        {tooltip && <TooltipIcon text={tooltip} position={tooltipPosition} side={tooltipSide} />}
      </div>
      <div className="relative">
        <input
          id={name} name={name} type="text" value={value}
          onChange={(e) => { onChange({ target: { name, value: e.target.value.toUpperCase() } }); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder={isLoading ? "..." : placeholder}
          autoComplete="off" disabled={isLoading}
          className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg text-white p-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all uppercase placeholder:normal-case placeholder:text-slate-500"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500">
           {isLoading ? <span className="animate-spin h-4 w-4 border-2 border-slate-500 border-t-transparent rounded-full"></span> : <HiSearch className="w-4 h-4" />}
        </div>
      </div>
      {isOpen && options.length > 0 && (
        <ul className="absolute z-[110] w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl max-h-60 overflow-y-auto custom-scrollbar backdrop-blur-md">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <li key={opt} onMouseDown={(e) => { e.preventDefault(); onChange({ target: { name, value: opt } }); setIsOpen(false); }} className={`px-4 py-2 text-sm cursor-pointer hover:bg-blue-600 hover:text-white transition-colors text-slate-300 ${opt === value ? 'bg-blue-500/20 text-blue-400' : ''}`}>{opt}</li>
            ))
          ) : (
            <li className="px-4 py-3 text-xs text-slate-500 flex items-center justify-center"><HiExclamationCircle className="mr-2" /> No symbols</li>
          )}
        </ul>
      )}
    </div>
  );
};

const StrategySettings = ({ initialSettings, onApply }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    symbol_a: '', symbol_b: '', timeframe: '5',
    zscore_period: '300', bar: '30000',
    margin: '100', leverage: '1', 
    total_usdt_one_entry: '', commission_rate: '0.1',
    exit: '0',
    shortLevels: '2, 3, 4', longLevels: '-2, -3, -4',
    dateFilterEnabled: false,
    dateFrom: '2025-10-09',
    dateTo: '2025-10-11'
  });

  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [isLoadingSymbols, setIsLoadingSymbols] = useState(false);

  // Расчет размера позиции
  const calculatedPositionSize = Number(settings.margin || 0) * Number(settings.leverage || 0);
  
  // Проверка на минимальную сумму (25$)
  const isPositionTooSmall = calculatedPositionSize < 25;

  // Расчет количества уровней для рекомендуемого баланса
  const getLevelsCount = (str) => {
    if (!str) return 0;
    return str.split(',').filter(s => s.trim() !== '').length;
  };

  const shortLevelsCount = getLevelsCount(settings.shortLevels);
  const longLevelsCount = getLevelsCount(settings.longLevels);
  // Берем максимальное количество уровней в одну сторону (так как обычно сетка идет в одну сторону)
  // Либо если логика стратегии подразумевает одновременные входы, можно суммировать. 
  // Но по вашему описанию: "берем одну шорт и одну лонг", и баланс $90 при $30 позиции (3 уровня) -> значит берем макс. кол-во уровней * позицию.
  const maxLevels = Math.max(shortLevelsCount, longLevelsCount) || 1;
  const recommendedBalance = calculatedPositionSize * maxLevels;

  useEffect(() => {
    const fetchSymbols = async () => {
      setIsLoadingSymbols(true);
      try {
        const token = localStorage.getItem('authToken'); 
        const response = await fetch('https://api.hedger.bar/api/candles/symbols?exchange=Bybit', {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableSymbols(Array.isArray(data) ? data : (data.symbols || []));
        }
      } catch (error) { console.error("Symbols fetch failed", error); } 
      finally { setIsLoadingSymbols(false); }
    };
    fetchSymbols();
  }, []);

  useEffect(() => {
    if (initialSettings) {
      setSettings(prev => ({
        ...prev,
        symbol_a: initialSettings.symbol_a || '',
        symbol_b: initialSettings.symbol_b || '',
        timeframe: String(initialSettings.timeframe || '5'),
        zscore_period: initialSettings.zscore_period ?? '300',
        bar: initialSettings.bar ?? '30000',
        margin: initialSettings.margin ?? '100', 
        leverage: initialSettings.leverage ?? '1', 
        total_usdt_one_entry: initialSettings.total_usdt_one_entry ?? '',
        commission_rate: initialSettings.commission_rate || '0.1',
        exit: initialSettings.exit !== undefined ? String(initialSettings.exit) : '0', 
        shortLevels: (Array.isArray(initialSettings.levels?.short) ? initialSettings.levels.short.join(', ') : '2, 3, 4'),
        longLevels: (Array.isArray(initialSettings.levels?.long) ? initialSettings.levels.long.join(', ') : '-2, -3, -4')
      }));
    }
  }, [initialSettings]); 

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    
    if (name === 'shortLevels') {
      setSettings(prev => ({ ...prev, [name]: value.replace(/[^0-9,. ]/g, '') }));
      return;
    }
    if (name === 'longLevels') {
      setSettings(prev => ({ ...prev, [name]: value.replace(/[^-0-9,. ]/g, '') }));
      return;
    }
    setSettings(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isPositionTooSmall) return; // Блокировка отправки

    const parseLevels = (str) => str ? String(str).split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n)) : [];
    
    const formatDateForBackend = (isoDate) => {
        if (!isoDate) return null;
        const [year, month, day] = isoDate.split('-');
        return `${day}.${month}.${year}`;
    };

    const finalParams = {
      symbol_a: settings.symbol_a,
      symbol_b: settings.symbol_b,
      timeframe: String(settings.timeframe),
      zscore_period: Number(settings.zscore_period),
      bar: Number(settings.bar), 
      margin: Number(settings.margin),
      leverage: Number(settings.leverage),
      total_usdt_one_entry: calculatedPositionSize, 
      commission_rate: Number(settings.commission_rate) / 100,
      exit: Number(settings.exit || 0), 
      with_date: settings.dateFilterEnabled ? formatDateForBackend(settings.dateFrom) : null,
      by_date: settings.dateFilterEnabled ? formatDateForBackend(settings.dateTo) : null,
      dateFilterEnabled: settings.dateFilterEnabled,
      dateFrom: settings.dateFrom,
      dateTo: settings.dateTo,
      level_entry: { 
        level_entry_positive: parseLevels(settings.shortLevels), 
        level_entry_negative: parseLevels(settings.longLevels) 
      },
      strategy_name: "pairs_trading", 
      strategy_method: "zscore"
    };

    if (onApply) onApply(finalParams);
  };

  return (
    <div className="bg-slate-800 p-6 rounded-xl border border-slate-700/50 shadow-xl h-full flex flex-col">
      <h3 className="text-lg font-semibold text-white mb-6">{t('strategy_settings.title')}</h3>
      <form onSubmit={handleSubmit} className="space-y-4 flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar p-1 pt-2">
        <div className="grid grid-cols-2 gap-4">
          <SymbolAutocomplete label={t('strategy_settings.symbol_a')} name="symbol_a" value={settings.symbol_a} onChange={handleChange} options={availableSymbols} isLoading={isLoadingSymbols} tooltip={t('strategy_settings.tooltips.symbol')} tooltipSide="bottom" />
          <SymbolAutocomplete label={t('strategy_settings.symbol_b')} name="symbol_b" value={settings.symbol_b} onChange={handleChange} options={availableSymbols} isLoading={isLoadingSymbols} tooltip={t('strategy_settings.tooltips.symbol')} tooltipPosition="right" tooltipSide="bottom" />
        </div>
        
        <CustomSelect 
          label={t('strategy_settings.timeframe')} 
          name="timeframe" 
          value={settings.timeframe} 
          onChange={handleChange} 
          options={[1, 3, 5, 15, 30, 60]} 
          tooltip={t('strategy_settings.tooltips.timeframe')} 
          tooltipSide="bottom" 
        />

        <div className="grid grid-cols-2 gap-4">
            <InputField label={t('strategy_settings.zscore')} name="zscore_period" type="number" value={settings.zscore_period} onChange={handleChange} tooltip={t('strategy_settings.tooltips.zscore')} />
            <InputField label={t('strategy_settings.bar_count')} name="bar" type="number" value={settings.bar} onChange={handleChange} tooltip={t('strategy_settings.tooltips.bar_count')} tooltipPosition="right" />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
            <InputField label={t('strategy_settings.margin')} name="margin" type="number" value={settings.margin} onChange={handleChange} tooltip={t('strategy_settings.tooltips.margin')} />
            <InputField label={t('strategy_settings.leverage')} name="leverage" type="number" value={settings.leverage} onChange={handleChange} tooltip={t('strategy_settings.tooltips.leverage')} tooltipPosition="right" />
        </div>

        <div className="grid grid-cols-2 gap-4">
            <InputField 
              label={t('strategy_settings.position_size')} 
              name="total_usdt_one_entry" 
              type="number" 
              value={calculatedPositionSize || ''} 
              readOnly={true} 
              tooltip={t('strategy_settings.tooltips.pos_size')}
              // Передаем ошибку если меньше 25
              error={isPositionTooSmall ? t('strategy_settings.min_size_error') : null}
            />
            <InputField label={t('strategy_settings.commission')} name="commission_rate" type="number" step="0.01" value={settings.commission_rate} onChange={handleChange} tooltip={t('strategy_settings.tooltips.commission')} tooltipPosition="right" />
        </div>
        
        <InputField label={t('strategy_settings.exit')} name="exit" type="number" step="0.1" value={settings.exit} onChange={handleChange} tooltip={t('strategy_settings.tooltips.exit')} />
        <InputField label={t('strategy_settings.entry_plus')} name="shortLevels" value={settings.shortLevels} onChange={handleChange} tooltip={t('strategy_settings.tooltips.entry_plus')} />
        <InputField label={t('strategy_settings.entry_minus')} name="longLevels" value={settings.longLevels} onChange={handleChange} tooltip={t('strategy_settings.tooltips.entry_minus')} />

        {/* БЛОК С РЕКОМЕНДУЕМЫМ БАЛАНСОМ */}
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mt-2">
            <div className="flex items-center gap-2 mb-1">
                <HiShieldCheck className="text-blue-400 w-5 h-5" />
                <span className="text-sm font-bold text-blue-200">
                    {t('strategy_settings.rec_balance_title')}
                </span>
            </div>
            <div className="flex items-end justify-between">
                <p className="text-[11px] text-blue-300/80 leading-tight max-w-[70%]">
                    {t('strategy_settings.rec_balance_desc', { count: maxLevels, size: calculatedPositionSize })}
                </p>
                <span className={`text-lg font-black ${recommendedBalance > 0 ? 'text-emerald-400' : 'text-slate-400'}`}>
                    ${recommendedBalance}
                </span>
            </div>
        </div>

        <div className="pt-2">
          <button 
            type="submit" 
            disabled={isPositionTooSmall}
            className={`w-full py-3 font-bold text-sm rounded-lg text-white transition-all active:scale-[0.98] 
              ${isPositionTooSmall 
                ? 'bg-slate-600 cursor-not-allowed opacity-50' 
                : 'bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-900/20'}`
            }
          >
            {t('strategy_settings.apply_btn')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StrategySettings;